import {
  ApprovalStatus,
  CopyTigerAdApproval,
  CopyTigerAdDecision as SharedAdDecision,
  CopyTigerLineRejection,
  CopyTigerRunApprovals,
  PushCopyTigerGoogleAdParams,
  PushCopyTigerGoogleAdResponse
} from '@blisspointmedia/bpm-types/dist/CopyTiger';
import { Intent, IntentType } from '../../Components/designConstants';
import { awaitJSON, CopyTigerLambdaFetch } from '../../utils/fetch-utils';
import { formatWholeUsd } from '../../utils/text-utils';

// Mirrors the run file bpm-python-lambdas writes (copytiger/copytigerRunStore.py, buildRunDocument,
// schemaVersion 1) at s3://adcopy-ai-runs/{company}/{runId}.json.
export interface CopyTigerAdCopy {
  headlines: string[];
  descriptions: string[];
}

export interface CopyTigerRunAd {
  current: CopyTigerAdCopy;
  generated: CopyTigerAdCopy;
  // The producer keeps runs with validation errors out of S3, so this is expected to be empty.
  validationErrors: string[];
  // Any extra columns from the generation rows (may include names like 'Ad Group Name'). Omitted when empty.
  context?: Record<string, unknown>;
  errors?: unknown;
  // Display names, present on newer runs.
  names?: { account: string; campaign: string; adGroup: string };
  // Why a scheduled run picked this ad, and copy warnings. Absent on on-demand and older runs.
  flagged?: CopyTigerAdFlagged;
  warnings?: string[];
}

export type CopyTigerFlagMetric =
  'spend' | 'conversions' | 'conversionsValue' | 'adStrength' | 'activeDays';

// threshold/delta/deltaPct come only with numeric values; deltaPct is left out when the threshold is 0.
export interface CopyTigerFlagReason {
  metric: CopyTigerFlagMetric;
  label: string;
  value: number | string;
  threshold?: number;
  delta?: number;
  deltaPct?: number;
}

// reasons[] is already in importance order.
export interface CopyTigerAdFlagged {
  summary: string;
  reasons: CopyTigerFlagReason[];
}

// ads[platform][accountId][campaignId][adGroupId][adId]
export type CopyTigerRunAds = Record<
  string,
  Record<string, Record<string, Record<string, Record<string, CopyTigerRunAd>>>>
>;

export interface CopyTigerRun {
  schemaVersion: number;
  runId: string;
  company: string;
  createdAt: string;
  trigger: 'onDemand' | 'scheduled';
  mode: string;
  model: {
    instanceUuid: string;
    styleGuideUuid: string | null;
    temperature: number;
    topK: number;
    topP: number;
  };
  additionalInstructions: string;
  summary: { adCount: number; adsWithValidationErrors: number };
  ads: CopyTigerRunAds;
}

export interface RunAdGroup {
  platform: string;
  accountId: string;
  campaignId: string;
  adGroupId: string;
  // From ad.names (newer runs) or the context columns; absent when the run only has ids.
  campaignName?: string;
  adGroupName?: string;
  ads: (CopyTigerRunAd & { adId: string })[];
}

const contextName = (ad: CopyTigerRunAd | undefined, key: string) => {
  const value = ad?.context?.[key];
  return typeof value === 'string' && value ? value : undefined;
};

// Flattens the nested run file into one entry per ad group for display.
export const getRunAdGroups = (run: CopyTigerRun): RunAdGroup[] =>
  Object.entries(run.ads).flatMap(([platform, accounts]) =>
    Object.entries(accounts).flatMap(([accountId, campaigns]) =>
      Object.entries(campaigns).flatMap(([campaignId, adGroups]) =>
        Object.entries(adGroups).map(([adGroupId, ads]) => {
          const adList = Object.entries(ads).map(([adId, ad]) => ({ ...ad, adId }));
          return {
            platform,
            accountId,
            campaignId,
            adGroupId,
            // Newer runs carry names in ad.names; older ones only in the context columns.
            campaignName: adList[0]?.names?.campaign || contextName(adList[0], 'Campaign Name'),
            adGroupName: adList[0]?.names?.adGroup || contextName(adList[0], 'Ad Group Name'),
            ads: adList
          };
        })
      )
    )
  );

export const STATUS_INTENT: Record<ApprovalStatus, IntentType> = {
  pending: Intent.WARNING,
  approved: Intent.SUCCESS,
  rejected: Intent.ERROR
};

// The shared decision, except the client UI never sends 'pending' and keeps each rejected line's `text` so the
// optimistic update can show it before the server snapshots it.
export type CopyTigerAdDecision = Omit<SharedAdDecision, 'status' | 'lineRejections'> & {
  status: Exclude<ApprovalStatus, 'pending'>;
  lineRejections?: CopyTigerLineRejection[];
};

// Dev-only demo: `?as=client` renders the review page as the client, and every copytiger call from it
// asks the lambda to treat the caller as one. Prod builds ignore the param.
export const isViewingAsClient = (search: string) =>
  import.meta.env.DEV && new URLSearchParams(search).get('as') === 'client';

const viewAsHeaders = () =>
  isViewingAsClient(window.location.search) ? { 'x-adcopy-view-as': 'client' } : undefined;

export const getCopyTigerRun = async (company: string, runId: string): Promise<CopyTigerRun> =>
  awaitJSON(
    await CopyTigerLambdaFetch('/getCopyTigerRun', {
      method: 'GET',
      params: { company, runId },
      headers: viewAsHeaders()
    })
  );

export const getCopyTigerRunApprovals = async (
  company: string,
  runId: string
): Promise<CopyTigerRunApprovals> =>
  awaitJSON(
    await CopyTigerLambdaFetch('/getCopyTigerRunApprovals', {
      method: 'GET',
      params: { company, runId },
      headers: viewAsHeaders()
    })
  );

// Returns the saved approval rows, shaped for the caller's stage like getCopyTigerRunApprovals.
export const setCopyTigerAdApprovals = async (
  company: string,
  runId: string,
  decisions: CopyTigerAdDecision[]
): Promise<CopyTigerAdApproval[]> =>
  awaitJSON(
    await CopyTigerLambdaFetch('/setCopyTigerAdApprovals', {
      method: 'POST',
      body: JSON.stringify({ company, runId, decisions }),
      headers: viewAsHeaders()
    })
  );

// Client role only. 409 if the run isn't sent, is already submitted, or still has pending ads.
export const submitCopyTigerClientReview = async (
  company: string,
  runId: string
): Promise<void> => {
  await CopyTigerLambdaFetch('/submitCopyTigerClientReview', {
    method: 'POST',
    body: JSON.stringify({ company, runId }),
    headers: viewAsHeaders()
  });
};

export const sendCopyTigerRunToClient = async (company: string, runId: string): Promise<void> => {
  await CopyTigerLambdaFetch('/sendCopyTigerRunToClient', {
    method: 'POST',
    body: JSON.stringify({ company, runId }),
    headers: viewAsHeaders()
  });
};

// Internal-only route, so no view-as header. Needs the pushAdCopyToGoogle flag and an allowlisted account.
// retries: 0 because each non-validate call creates a new ad.
export const pushTestAdToGoogle = async (
  body: PushCopyTigerGoogleAdParams
): Promise<PushCopyTigerGoogleAdResponse> =>
  awaitJSON(
    await CopyTigerLambdaFetch('/pushTestAdToGoogle', {
      method: 'POST',
      body: JSON.stringify(body),
      retries: 0
    })
  );

// Identifies an ad across the run JSON (camelCase) and the approvals rows (snake_case).
export const adKey = (platform: string, accountId: string, adGroupId: string, adId: string) =>
  [platform, accountId, adGroupId, adId].join('|');

export const approvalKey = (a: CopyTigerAdApproval) =>
  adKey(a.platform, a.accountId, a.adGroupId, a.adId);

export type CopyMark = 'kept' | 'new' | 'removed';

export interface MarkedLine {
  text: string;
  mark: CopyMark;
}

const normalizeCopy = (text: string) => text.trim().toLowerCase();

// Generated copy is a whole new set rather than edits to existing slots, so lines are compared as sets:
// a line on either side that exactly matches one on the other (ignoring case and outer spaces) is kept.
export const markCopy = (current: string[], generated: string[]) => {
  const currentSet = new Set(current.map(normalizeCopy));
  const generatedSet = new Set(generated.map(normalizeCopy));
  return {
    current: current.map((text): MarkedLine => ({
      text,
      mark: generatedSet.has(normalizeCopy(text)) ? 'kept' : 'removed'
    })),
    generated: generated.map((text): MarkedLine => ({
      text,
      mark: currentSet.has(normalizeCopy(text)) ? 'kept' : 'new'
    }))
  };
};

// context.keywords is a comma-separated string in the producer's rows.
export const getAdKeywords = (ad: CopyTigerRunAd): string[] => {
  const keywords = ad.context?.keywords;
  return typeof keywords === 'string'
    ? keywords
        .split(',')
        .map(k => k.trim())
        .filter(Boolean)
    : [];
};

// One chip per reason, e.g. 'Spend $1,240 (+$1,140 vs $100)' or '0 conv (≤ 1)'. Spend is flagged at or
// above its minimum; conversions and conversion value at or below theirs; days active is informational.
export const formatFlagReason = ({
  metric,
  label,
  value,
  threshold,
  delta
}: CopyTigerFlagReason) => {
  if (typeof value === 'string') {
    return metric === 'adStrength' ? `${value} strength` : `${label} ${value}`;
  }
  const count = value.toLocaleString('en-US');
  switch (metric) {
    case 'spend':
      return threshold == null || delta == null
        ? `Spend ${formatWholeUsd(value)}`
        : `Spend ${formatWholeUsd(value)} (+${formatWholeUsd(delta)} vs ${formatWholeUsd(threshold)})`;
    case 'conversions':
      return `${count} conv${threshold == null ? '' : ` (≤ ${threshold.toLocaleString('en-US')})`}`;
    case 'conversionsValue':
      return `Conv value ${formatWholeUsd(value)}${threshold == null ? '' : ` (≤ ${formatWholeUsd(threshold)})`}`;
    case 'activeDays':
      return `${count} days active`;
    default:
      return `${label} ${count}`;
  }
};
